/**
 * DependencyContainer
 *
 * Enterprise-grade dependency injection container
 * Wires up all services with their dependencies
 *
 * Usage:
 * ```ts
 * const container = new DependencyContainer(config);
 * const adapter = container.createEdgeStreamAdapter();
 * ```
 */

import type {
  ISignalRConnection,
  IHandlerRegistry,
  IMessageTransformer,
  IPipelineOrchestrator,
  ISubscriberDistributor,
  ILoggingStrategy,
  IReconnectionStrategy,
} from './interfaces';

import {
  SignalRConnectionService,
  HandlerRegistryService,
  MessageEnvelopeFactory,
  SubscriberDistributorService,
  ExponentialBackoffStrategy,
  StructuredLoggingStrategy,
  PipelineOrchestratorService,
} from './implementations';

import { SignalRConfiguration } from './config/SignalRConfiguration';
import type { SignalRSettings } from './config/SignalRConfiguration';

export interface ContainerConfig {
  signalrSettings?: Partial<SignalRSettings>;
  enableLogging?: boolean;
  logLevel?: 'debug' | 'info' | 'warn' | 'error';
}

/**
 * Service registry for dependency injection
 */
export class DependencyContainer {
  private config: SignalRConfiguration;
  private enableLogging: boolean;
  private singletons: Map<string, unknown> = new Map();

  constructor(containerConfig: ContainerConfig = {}) {
    this.config = new SignalRConfiguration(containerConfig.signalrSettings);
    this.enableLogging = containerConfig.enableLogging ?? true;

    // Validate configuration
    const errors = this.config.validate();
    if (errors.length > 0) {
      console.warn('[DependencyContainer] Configuration validation warnings:', errors);
    }
  }

  /**
   * Get configuration instance
   */
  getConfig(): SignalRConfiguration {
    return this.config;
  }

  /**
   * Create or get singleton SignalRConnection service
   */
  getSignalRConnection(): ISignalRConnection {
    if (!this.singletons.has('connection')) {
      this.singletons.set('connection', new SignalRConnectionService());
    }
    return this.singletons.get('connection') as ISignalRConnection;
  }

  /**
   * Create or get singleton HandlerRegistry service
   */
  getHandlerRegistry(): IHandlerRegistry {
    if (!this.singletons.has('handlerRegistry')) {
      this.singletons.set('handlerRegistry', new HandlerRegistryService());
    }
    return this.singletons.get('handlerRegistry') as IHandlerRegistry;
  }

  /**
   * Create or get singleton MessageTransformer service
   */
  getMessageTransformer(): IMessageTransformer {
    if (!this.singletons.has('transformer')) {
      this.singletons.set('transformer', new MessageEnvelopeFactory());
    }
    return this.singletons.get('transformer') as IMessageTransformer;
  }

  /**
   * Create or get singleton SubscriberDistributor service
   */
  getSubscriberDistributor(): ISubscriberDistributor {
    if (!this.singletons.has('distributor')) {
      this.singletons.set('distributor', new SubscriberDistributorService());
    }
    return this.singletons.get('distributor') as ISubscriberDistributor;
  }

  /**
   * Create or get singleton LoggingStrategy service
   */
  getLoggingStrategy(): ILoggingStrategy {
    if (!this.singletons.has('logger')) {
      const logger = new StructuredLoggingStrategy();
      if (!this.enableLogging) {
        logger.setEnabled(false);
      }
      this.singletons.set('logger', logger);
    }
    return this.singletons.get('logger') as ILoggingStrategy;
  }

  /**
   * Create or get singleton ReconnectionStrategy service
   */
  getReconnectionStrategy(): IReconnectionStrategy {
    if (!this.singletons.has('reconnect')) {
      const reconnectConfig = this.config.get<Record<string, unknown>>('reconnection');
      this.singletons.set(
        'reconnect',
        new ExponentialBackoffStrategy(reconnectConfig as any)
      );
    }
    return this.singletons.get('reconnect') as IReconnectionStrategy;
  }

  /**
   * Create or get singleton PipelineOrchestrator service
   * Note: This requires pipelines to be provided
   */
  getPipelineOrchestrator(
    incomingPipeline: any,
    outgoingPipeline: any
  ): IPipelineOrchestrator {
    if (!this.singletons.has('orchestrator')) {
      this.singletons.set(
        'orchestrator',
        new PipelineOrchestratorService(
          incomingPipeline,
          outgoingPipeline,
          this.getLoggingStrategy()
        )
      );
    }
    return this.singletons.get('orchestrator') as IPipelineOrchestrator;
  }

  /**
   * Reset all services
   * Useful for testing
   */
  reset(): void {
    this.singletons.clear();
  }

  /**
   * Get all registered services
   */
  getServices(): Record<string, unknown> {
    return {
      config: this.config,
      connection: this.getSignalRConnection(),
      handlerRegistry: this.getHandlerRegistry(),
      messageTransformer: this.getMessageTransformer(),
      subscriberDistributor: this.getSubscriberDistributor(),
      loggingStrategy: this.getLoggingStrategy(),
      reconnectionStrategy: this.getReconnectionStrategy(),
    };
  }

  /**
   * Get diagnostics about all services
   */
  getDiagnostics(): {
    config: Readonly<SignalRSettings>;
    services: Record<string, unknown>;
    stats: Record<string, unknown>;
  } {
    const connection = this.getSignalRConnection();
    const registry = this.getHandlerRegistry();
    const transformer = this.getMessageTransformer();
    const distributor = this.getSubscriberDistributor();
    const logger = this.getLoggingStrategy();
    const reconnect = this.getReconnectionStrategy();

    return {
      config: this.config.getSettings(),
      services: {
        connection: {
          state: (connection as any).state,
          isConnected: connection.isConnected(),
        },
        handlerRegistry: registry.getDiagnostics(),
        messageTransformer: (transformer as any).getStatistics(),
        subscriberDistributor: distributor.getDiagnostics(),
        loggingStrategy: logger.getStatistics(),
        reconnectionStrategy: (reconnect as any).getStatistics(),
      },
      stats: {
        singletonCount: this.singletons.size,
        loggingEnabled: this.enableLogging,
      },
    };
  }
}

/**
 * Global container instance (optional)
 * Apps can use their own instance or this shared one
 */
export const globalContainer = new DependencyContainer();
